import '../pages.css';
import useDocumentMeta from '../hooks/useDocumentMeta.js';

// Mirrors the plans on the Shopify app listing. Every plan carries the full
// feature set; the tiers differ only on order volume, included address
// validations and support level.
const TIERS = [
  {
    num: '01',
    name: 'Starter',
    range: ['0 – 1,200 orders', 'per month'],
    price: '$39',
    annual: '$374.40/year — save 20%',
    feats: [
      'Access to every feature',
      'Unlimited order edits',
      '50 free address validations',
      'Email + chat support',
    ],
  },
  {
    num: '02',
    name: 'Growth',
    rec: true,
    range: ['1,201 – 5,000 orders', 'per month'],
    price: '$99',
    annual: '$950.40/year — save 20%',
    feats: [
      'Access to every feature',
      'Unlimited order edits',
      '200 free address validations',
      'Priority support (24hr response)',
    ],
  },
  {
    num: '03',
    name: 'Scale',
    range: ['5,001 – 10,000 orders', 'per month'],
    price: '$179',
    annual: '$1,718.40/year — save 20%',
    feats: [
      'Access to every feature',
      'Unlimited order edits',
      '350 free address validations',
      'Priority support + Slack channel',
    ],
  },
  {
    num: '04',
    name: 'Enterprise',
    range: ['10,001 – 20,000 orders', 'per month'],
    price: '$249',
    annual: '$2,390.40/year — save 20%',
    feats: [
      'Access to every feature',
      'Unlimited order edits',
      '500 free address validations',
      'Priority support + Slack channel',
    ],
    cta: { label: 'Talk to sales', href: '/contact-sales' },
  },
];

const TRUST = [
  { icon: 'ph-arrows-down-up', text: ['Change plans', 'anytime'] },
  { icon: 'ph-x-circle', text: ['Cancel', 'anytime'] },
  { icon: 'ph-lock-simple', text: ['Secure &', 'reliable'] },
  { icon: 'ph-headset', text: ['Support when', 'you need it'] },
];

const PLAN_COLS = [
  { name: 'Starter', price: '$39/mo' },
  { name: 'Growth', price: '$99/mo', rec: true },
  { name: 'Scale', price: '$179/mo' },
  { name: 'Enterprise', price: '$249/mo' },
];

// value per plan: true = check, false = dash, string = text
const COMPARE = [
  { feat: 'Orders / month', vals: ['0–1,200', '1,201–5,000', '5,001–10,000', '10,001–20,000'] },
  { feat: 'Billed yearly', vals: ['$374.40', '$950.40', '$1,718.40', '$2,390.40'] },
  { feat: 'Unlimited order edits', vals: [true, true, true, true] },
  { feat: 'Edit shipping address', vals: [true, true, true, true] },
  { feat: 'Discount recalculation', vals: [true, true, true, true] },
  { feat: 'One click upsell', vals: [true, true, true, true] },
  { feat: 'Address validations & suggestions', vals: [true, true, true, true] },
  { feat: 'Free address validations', vals: ['50', '200', '350', '500'] },
  {
    feat: 'Additional validations',
    vals: ['$0.01–$0.03', '$0.01–$0.03', '$0.01–$0.03', '$0.01–$0.03'],
  },
  {
    feat: 'Support',
    vals: ['Email + chat', 'Priority (24hr)', 'Priority + Slack', 'Priority + Slack'],
  },
  { feat: 'Free trial', vals: ['14 days', '14 days', '14 days', '14 days'] },
];

function Cell({ v }) {
  if (v === true) return <span className="pcmp__yes"><i className="ph-fill ph-check-fat" aria-hidden="true" /></span>;
  if (v === false) return <span className="pcmp__no">—</span>;
  return <span>{v}</span>;
}

export default function PricingPlan() {
  useDocumentMeta({
    title: 'Best Shopify Order Editing App Pricing | Account Editor',
    description:
      'Compare Account Editor pricing plans for your Shopify store. Find the right fit for your order volume, from Starter and Growth to Scale and Enterprise.',
    path: '/pricing',
  });

  return (
    <div className="aehome">
      <section className="prc" id="prcSec">
        <div className="prc__inner">
          <svg className="prc__sweep" viewBox="0 0 1340 520" preserveAspectRatio="none" aria-hidden="true">
            <path className="prc__sweep-path" d="M 120 430 C 470 430 800 330 1180 60 L 1143 66 L 1180 60 L 1162 93" />
          </svg>
          <div className="prc__head">
            <h1 className="prc__title">Pricing that scales with you<em>.</em></h1>
            <p className="prc__sub">Only pay for what you use. Upgrade, downgrade, or cancel anytime.</p>
          </div>
          <div className="prc__ladder">
            {TIERS.map((t) => (
              <div className={`prc__step${t.rec ? ' prc__step--rec' : ''}`} key={t.num}>
                {t.rec && <span className="prc__ribbon">Recommended</span>}
                <div className="prc__step-top"><span className="prc__num">{t.num}</span><span className="prc__name">{t.name}</span></div>
                <div className="prc__range">{t.range[0]}<br />{t.range[1]}</div>
                <hr className="prc__hr" />
                <div className="prc__price"><b>{t.price}</b><span>/ month</span></div>
                {t.annual && <p className="prc__annual">or {t.annual}</p>}
                <hr className="prc__hr" />
                <ul className="prc__feats">
                  {t.feats.map((f) => (
                    <li key={f}><span className="prc__chk"><i className="ph-fill ph-check-fat" aria-hidden="true" /></span>{f}</li>
                  ))}
                </ul>
                {t.cta && (
                  <a href={t.cta.href} className="btn btn--primary-lg prc__step-cta">{t.cta.label}</a>
                )}
              </div>
            ))}
          </div>
          <div className="prc__trust">
            {TRUST.map((tr) => (
              <div className="prc__trust-item" key={tr.text.join(' ')}>
                <i className={`ph ${tr.icon}`} aria-hidden="true" />
                <span>{tr.text[0]}<br />{tr.text[1]}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pcmp">
        <div className="pcmp__inner">
          <div className="pcmp__head">
            <h2 className="pcmp__title">Compare every plan</h2>
            <p className="pcmp__sub">Everything each plan includes, side by side.</p>
          </div>
          <div className="pcmp__scroll">
            <table className="pcmp__table">
              <thead>
                <tr>
                  <th className="pcmp__feat">Features</th>
                  {PLAN_COLS.map((p) => (
                    <th key={p.name} className={p.rec ? 'pcmp__col--rec' : undefined}>
                      {p.name}<small>{p.price}</small>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row) => (
                  <tr key={row.feat}>
                    <td className="pcmp__feat">{row.feat}</td>
                    {row.vals.map((v, i) => (
                      <td key={i} className={PLAN_COLS[i].rec ? 'pcmp__col--rec' : undefined}>
                        <Cell v={v} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
